<template>
  <UFileUpload
    ref="fileUpload"
    v-bind="{ ...props, ...$attrs }"
    v-model="model"
    :ui="{
      ...props.ui,
      fileTrailingButton: normalizeClass([
        typeof props.fileDelete === 'boolean' && 'text-on-surface dark:text-on-surface-dark',
        props.ui?.fileTrailingButton
      ])
    }"
    class="p-file-upload"
    @change="emit('change', $event)"
    @reject="emit('reject', $event)"
    @cancel="emit('cancel', $event)"
  >
    <template v-if="$slots.default" #default="slotProps"><slot v-bind="slotProps" /></template>
    <template v-if="$slots.leading" #leading="slotProps">
      <slot name="leading" v-bind="slotProps" />
    </template>
    <template v-if="$slots.label" #label><slot name="label" /></template>
    <template v-if="$slots.description" #description><slot name="description" /></template>
    <template v-if="$slots.actions" #actions="slotProps">
      <slot name="actions" v-bind="slotProps" />
    </template>
    <template v-if="$slots.files" #files="slotProps">
      <slot name="files" v-bind="slotProps" />
    </template>
    <template v-if="$slots['files-top']" #files-top="slotProps">
      <slot name="files-top" v-bind="slotProps" />
    </template>
    <template v-if="$slots['files-bottom']" #files-bottom="slotProps">
      <slot name="files-bottom" v-bind="slotProps" />
    </template>
    <template v-if="$slots.file" #file="slotProps">
      <slot name="file" v-bind="slotProps" />
    </template>
    <template v-if="$slots['file-leading']" #file-leading="slotProps">
      <slot name="file-leading" v-bind="slotProps" />
    </template>
    <template v-if="$slots['file-name']" #file-name="slotProps">
      <slot name="file-name" v-bind="slotProps" />
    </template>
    <template v-if="$slots['file-size']" #file-size="slotProps">
      <slot name="file-size" v-bind="slotProps" />
    </template>
    <template v-if="$slots['file-trailing']" #file-trailing="slotProps">
      <slot name="file-trailing" v-bind="slotProps" />
    </template>
  </UFileUpload>
</template>

<script setup lang="ts" generic="M extends boolean = false">
import { normalizeClass, useTemplateRef } from "vue"
import NuxtFileUpload, {
  type FileUploadProps,
  type FileUploadSlots,
  type FileUploadEmits
} from "@nuxt/ui/components/FileUpload.vue"
import type { ComponentColor } from "@/config/component-colors"

const UFileUpload = NuxtFileUpload<M, File>
const props = withDefaults(
  defineProps<
    Omit<FileUploadProps<M>, "color" | "modelValue"> & {
      color?: ComponentColor
      /** Request the front or rear camera when supported by the native file picker. */
      capture?: "user" | "environment"
    }
  >(),
  {
    color: "secondary",
    size: "md",
    icon: undefined,
    dropzone: true,
    interactive: true,
    preview: true,
    fileImage: true,
    fileDelete: true
  }
)

const model = defineModel<(M extends true ? File[] : File) | null>()
const emit = defineEmits<FileUploadEmits & { cancel: [event: Event] }>()
defineSlots<FileUploadSlots<M>>()
defineOptions({ inheritAttrs: false })

const fileUpload = useTemplateRef<{
  inputRef: HTMLInputElement | null
  dropzoneRef: HTMLElement | undefined
}>("fileUpload")

function open() {
  const input = fileUpload.value?.inputRef
  if (!input || input.disabled) return
  if (props.reset) input.value = ""
  input.click()
}

defineExpose({
  open,
  focus: () => fileUpload.value?.dropzoneRef?.focus(),
  get inputRef() {
    return fileUpload.value?.inputRef ?? null
  },
  get dropzoneRef() {
    return fileUpload.value?.dropzoneRef
  }
})
</script>
